/**
 * The sign-in return route (CNPWKS-0002). saas-auth's callback-path gate (SASATH-0156) admits only
 * `/auth/callback` (or `/auth/return`) as the returnUrl PATH for Google/Microsoft sign-in, so the
 * SPA returns here and carries the page it left in `next`.
 *
 * `next` is followed ONLY as a same-origin relative path, and ONLY via history.replaceState —
 * never location.assign, never an absolute URL (open-redirect defence).
 */
export const CALLBACK_PATH = '/auth/callback';

/** The page to come back to: `pathname + search`, or '/' when already on the callback path. */
export function currentNext(location: Pick<Location, 'pathname' | 'search'>): string {
  return location.pathname === CALLBACK_PATH ? '/' : `${location.pathname}${location.search}`;
}

/** returnUrl for centralized login: `${origin}/auth/callback?next=<encoded next>`. */
export function callbackReturnUrl(
  location: Pick<Location, 'origin' | 'pathname' | 'search'>,
): string {
  return `${location.origin}${CALLBACK_PATH}?next=${encodeURIComponent(currentNext(location))}`;
}

/**
 * A safe landing path for `next`, or '/'. Safe means: starts with '/', not '//' or '/\', resolves
 * to the SAME origin, and is not the callback path itself. Returns pathname + search only.
 */
export function safeNext(raw: string | null, origin: string): string {
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return '/';
  }
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return '/';
  }
  if (url.origin !== origin || url.pathname === CALLBACK_PATH) return '/';
  return `${url.pathname}${url.search}`;
}

/**
 * Called ONCE at boot. On the callback path: replace the URL with safeNext(next) (dropping every
 * parameter and fragment saas-auth appended) and return true. Anywhere else: touch nothing, false.
 */
export function consumeCallback(): boolean {
  if (window.location.pathname !== CALLBACK_PATH) return false;
  const next = new URLSearchParams(window.location.search).get('next');
  window.history.replaceState(null, '', safeNext(next, window.location.origin));
  return true;
}
